
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import './SearchRibbon.css';
import { IMainNavigationItem } from '../HeaderComponent/HeaderComponent';
import { useWidthContext } from '../../providers/Width';

import GoogleSearchInput, { CloseSearchIcon, SearchIcon } from '../GoogleSearchInput/GoogleSearchInput';
import { wrapTrademarkSuperscript } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import AdaptableTitleTagContainer from './utils/AdaptableTitleTagContainer';
import NavBarLinks from './utils/NavBarLinks';
import { Button, Container } from '../../importCL';
import RibbonDropdown from './utils/RibbonDropdown';
import ToggleDropdownButton from './utils/ToggleDropdownButton';

interface ISearchRibbonProps extends MappedComponentProperties {
    title?: string;
    titleLink?: string;
    titleTag?: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span';
    mobileLinkLabel?: string;
    navBarLinks?: IMainNavigationItem[];
    placeHolderText?: string;
    mobilePlaceHolderText?: string;
}

export const SearchRibbonEditConfig = {
    emptyLabel: 'Search Ribbon',
    isEmpty: (props: PropsWithChildren<ISearchRibbonProps>) => {
        return !props || !props.title;
    },
};

const SearchRibbon = (props: PropsWithChildren<ISearchRibbonProps>): JSX.Element => {
    const path = window.location.pathname;
    const isAuthor = (window.location.host.indexOf('4502') > -1) || (window.location.host.indexOf('3000') > -1);

    const TitleTag = props.titleTag || 'span';
    const [showSearchInput, setShowSearchInput] = useState<boolean>(false);
    const [inputVal, setInputVal] = useState<string>('');
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLUListElement>(null);
    const googleSearchInputRef = useRef<HTMLElement>(null);
    const searchIconRef = useRef<HTMLElement>(null);

    const { width } = useWidthContext();
    const desktop = width > 1199;
    const mobile = width < 769;
    const tablet = !(desktop || mobile);

    const shouldShowContainer = (desktop && showSearchInput) || (mobile && (showSearchInput || props.navBarLinks));
    const titleSpaceBetween = !props.navBarLinks && (!tablet || tablet && !showSearchInput);

    // Keep FAQ before help so FAQ routes containing "help" still resolve to FAQ search.
    const searchPages = ['articles', 'faqs', 'help', 'corporate/about-us/news-press', 'credit-cards', 'corporate/about-us'];
    const section = searchPages.find((page) => path.includes(page)) || '';
    const pageToRedirect = `${section}/search`;

    const closeIconClickCallback = (close: string) => {
        setInputVal('');
        if ((close === 'inputX' && !desktop) || close === 'ribbonX') {
            setShowSearchInput(false);
            setIsOpen(false);
        }
        if (close === 'inputX' && desktop) {
            googleSearchInputRef?.current?.focus();
            return;
        }
        setTimeout(() => {
            searchIconRef?.current?.focus();
        }, 100);
    };

    const searchIconClickCallback = () => {
        setShowSearchInput(true);
        setTimeout(() => {
            googleSearchInputRef?.current?.focus();
        }, 100);
    };

    const redirectToResults = () => {
        const resultsUrl = isAuthor ? '/content/cob-corp-acquisition/us/en/' + pageToRedirect + '.html' : '/' + pageToRedirect;

        if (!inputVal) {
            return;
        }
        const url = `${resultsUrl}?q=${inputVal}`;
        window.location.assign(url);
    };

    useEffect(() => {
        if (path.includes('search') && !path.includes('glossary')) {
            const urlParams = new URLSearchParams(window.location.search);
            const paramValue = urlParams.get('q');
            if (paramValue) {
                setShowSearchInput(true);
                setInputVal(paramValue);
            }
        }
    }, []);

    return (
        <header id='search-ribbon' aria-label='Search Ribbon' className='SearchRibbon'>
            <Container className='SearchRibbon__container'>
                <nav className='SearchRibbon__navBar'>
                    {(props.title || !props.navBarLinks) && (
                        <div className={`SearchRibbon__textContainer ${titleSpaceBetween ? 'SearchRibbon__textContainer--spaceBetween' : ''}`}>
                            {props.title &&
                                (<AdaptableTitleTagContainer className='SearchRibbon__text' href={props.titleLink}>
                                    <TitleTag>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}</TitleTag>
                                </AdaptableTitleTagContainer>
                                )}
                            {!props.navBarLinks &&
                                <div className='SearchRibbon__overlap'>
                                    {!showSearchInput && <div className='SearchRibbon__iconContainer' >
                                        <SearchIcon ref={searchIconRef} role='button' label='Open Search' tabIndex={0} className='SearchRibbon__icon SearchRibbon__searchIcon ' onClick={searchIconClickCallback} />
                                    </div>}
                                    {(showSearchInput && !tablet) && <CloseSearchIcon label='Close Search' tabIndex={0} className='SearchRibbon__icon' onClick={() => closeIconClickCallback('ribbonX')} />}
                                </div>
                            }
                        </div>
                    )}
                    {!mobile &&
                        <div className={`SearchRibbon__rightColumn ${!props.navBarLinks && !showSearchInput ? 'SearchRibbon__rightColumn--noLinks' : ''}`}>

                            {props.navBarLinks && <>
                                <div className='flex mb-0 items-center'>
                                    <NavBarLinks navBarLinks={props.navBarLinks} pathForActiveNavLink={path} />
                                </div>
                                {
                                    props.mobileLinkLabel &&
                                    !showSearchInput &&
                                    <div className='SearchRibbon__buttons'>
                                        <ToggleDropdownButton
                                            isOpen={isOpen}
                                            setIsOpen={setIsOpen}
                                            linkLabel={props.mobileLinkLabel}
                                        />
                                        <RibbonDropdown
                                            navBarLinks={props.navBarLinks}
                                            ref={ref}
                                            isOpen={isOpen}
                                            showSearchInput={showSearchInput}
                                        />
                                    </div>
                                }

                                <div className='SearchRibbon__overlap'>
                                    {!showSearchInput && <div className='SearchRibbon__iconContainer' >
                                        <SearchIcon ref={searchIconRef} role='button' label='Open Search' tabIndex={0} className='SearchRibbon__icon SearchRibbon__searchIcon ' onClick={searchIconClickCallback} />
                                    </div>}
                                    {(showSearchInput && !tablet) && <CloseSearchIcon label='Close Search' tabIndex={0} className='SearchRibbon__icon' onClick={() => closeIconClickCallback('ribbonX')} />}
                                </div>
                            </>
                            }
                            {tablet && showSearchInput &&
                                <GoogleSearchInput
                                    ref={googleSearchInputRef}
                                    aria-label='search'
                                    resultsUrl={pageToRedirect}
                                    placeHolderText={props.placeHolderText || ''}
                                    showX={true}
                                    closeSearchLabel='Close Search'
                                    inputValue={inputVal}
                                    setInputValue={setInputVal}
                                    onXClick={() => closeIconClickCallback('inputX')} />
                            }


                        </div>
                    }
                </nav>
            </Container>

            {
                !tablet &&
                <Container className={`SearchRibbon__panelSearch ${props.navBarLinks ? '' : 'SearchRibbon__panelSearch--right'} ${shouldShowContainer ? 'SearchRibbon__panelSearch--show' : ''}`}>
                    <div className={`SearchRibbon__searchDiv ${desktop ? 'SearchRibbon__desktop--centerAlign' : ''} ${mobile && !showSearchInput ? 'px-4' : ''}`}>
                        {
                            desktop || showSearchInput ?
                                <div className='SearchRibbon__desktopContainer'>
                                    <GoogleSearchInput
                                        ref={googleSearchInputRef}
                                        aria-label='search'
                                        resultsUrl={pageToRedirect}
                                        placeHolderText={desktop && props.placeHolderText || mobile && props.mobilePlaceHolderText || ''}
                                        showX={props.navBarLinks?.length && mobile || desktop && inputVal ? true : false}
                                        className='SearchRibbon__googleSearch' inputValue={inputVal} setInputValue={setInputVal} onXClick={() => closeIconClickCallback('inputX')} />
                                    {desktop && <Button className='SearchRibbon__searchCTA' variant='solid' size='medium' onClick={redirectToResults}>Search</Button>}
                                </div>
                                :
                                <>
                                    {props.navBarLinks && (
                                        <>
                                            <div className='SearchRibbon__buttons' >
                                                <ToggleDropdownButton
                                                    isOpen={isOpen}
                                                    setIsOpen={setIsOpen}
                                                    linkLabel={props.mobileLinkLabel}
                                                />
                                            </div>
                                            <div className='SearchRibbon__overlap'>
                                                {/* this SearchIcon is displayed in the search input field and is not interactable */}
                                                {!showSearchInput
                                                    ? <div className='SearchRibbon__iconContainer' >
                                                        <SearchIcon ref={searchIconRef} role='button' label='Open Search' tabIndex={0} className='SearchRibbon__icon SearchRibbon__searchIcon ' onClick={searchIconClickCallback} />
                                                    </div>
                                                    : <CloseSearchIcon label='Close Search' tabIndex={0} className='SearchRibbon__icon' onClick={() => closeIconClickCallback('ribbonX')} />
                                                }
                                            </div>
                                        </>
                                    )}
                                </>
                        }
                    </div>

                    {
                        mobile && props.navBarLinks &&
                        <RibbonDropdown
                            navBarLinks={props.navBarLinks}
                            ref={ref}
                            isOpen={isOpen}
                            showSearchInput={showSearchInput}
                        />
                    }
                </Container>
            }
        </header>
    );
};

export default SearchRibbon;
